import React from 'react';
import {
  Paper,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';

interface FieldStrategy {
  name?: 'prefer-target' | 'prefer-longest' | 'prefer-non-null';
  email?: 'prefer-target' | 'prefer-non-null';
  phone?: 'prefer-target' | 'prefer-non-null';
  address?: 'prefer-target' | 'prefer-longest' | 'prefer-non-null';
  nationality?: 'prefer-target' | 'prefer-non-null';
  types?: 'merge-all' | 'prefer-target' | 'prefer-source';
}

interface FieldMergeStrategyProps {
  target: any;
  sources: any[];
  strategy: FieldStrategy;
  onChange: (strategy: FieldStrategy) => void;
}

const FieldMergeStrategy: React.FC<FieldMergeStrategyProps> = ({
  target,
  sources,
  strategy,
  onChange,
}) => {
  const getFieldValue = (person: any, field: string): string => {
    const value = person[field];
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    return value || '—';
  };

  const getMergedValue = (field: string): string => {
    const strategyValue = strategy[field as keyof FieldStrategy];
    const targetValue = getFieldValue(target, field);
    const sourceValues = sources.map(s => getFieldValue(s, field)).filter(v => v !== '—');

    if (field === 'types') {
      if (strategyValue === 'merge-all') {
        const allTypes = new Set([
          ...(target.types || []),
          ...sources.flatMap(s => s.types || []),
        ]);
        return Array.from(allTypes).join(', ');
      }
      return targetValue;
    }

    if (strategyValue === 'prefer-target') {
      return targetValue !== '—' ? targetValue : sourceValues[0] || '—';
    }
    if (strategyValue === 'prefer-non-null') {
      return targetValue !== '—' ? targetValue : sourceValues[0] || '—';
    }
    if (strategyValue === 'prefer-longest') {
      const all = [targetValue, ...sourceValues].filter(v => v !== '—');
      return all.sort((a, b) => b.length - a.length)[0] || '—';
    }

    return targetValue;
  };

  const updateStrategy = (field: keyof FieldStrategy, value: any) => {
    onChange({ ...strategy, [field]: value });
  };

  const fields = [
    {
      key: 'name' as const,
      label: 'Name',
      targetOptions: ['prefer-target', 'prefer-longest', 'prefer-non-null'],
    },
    {
      key: 'email' as const,
      label: 'Email',
      targetOptions: ['prefer-target', 'prefer-non-null'],
    },
    {
      key: 'phone' as const,
      label: 'Phone',
      targetOptions: ['prefer-target', 'prefer-non-null'],
    },
    {
      key: 'address' as const,
      label: 'Address',
      targetOptions: ['prefer-target', 'prefer-longest', 'prefer-non-null'],
    },
    {
      key: 'nationality' as const,
      label: 'Nationality',
      targetOptions: ['prefer-target', 'prefer-non-null'],
    },
    {
      key: 'types' as const,
      label: 'Types',
      targetOptions: ['merge-all', 'prefer-target', 'prefer-source'],
    },
  ];

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
        Field Merge Strategy
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Field</TableCell>
            <TableCell>Target</TableCell>
            <TableCell>Source{sources.length > 1 ? 's' : ''}</TableCell>
            <TableCell>Strategy</TableCell>
            <TableCell>Result</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {fields.map((field) => {
            const sourceValues = sources.map(s => getFieldValue(s, field.key));
            const uniqueSourceValues = Array.from(new Set(sourceValues.filter(v => v !== '—')));
            
            return (
              <TableRow key={field.key}>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {field.label}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {getFieldValue(target, field.key)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {uniqueSourceValues.length > 0
                      ? uniqueSourceValues.join('; ')
                      : '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <Select
                      value={strategy[field.key] || 'prefer-target'}
                      onChange={(e) => updateStrategy(field.key, e.target.value)}
                    >
                      {field.targetOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option
                            .split('-')
                            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                            .join(' ')}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold" color="primary.main">
                    {getMergedValue(field.key)}
                  </Typography>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Paper>
  );
};

export default FieldMergeStrategy;
